<script>
import {computed, defineComponent, onBeforeMount, reactive, ref} from "vue";
import {useStore} from "vuex";

import Button from "primevue/button";
import InputText from "primevue/inputtext";
import Textarea from 'primevue/textarea';
import Dropdown from "primevue/dropdown";
import MainCard from "@/components/cards/MainCard";
import Breadcrumb from "@/components/Breadcrumb";
import ButtonSuccess from "@/components/buttons/ButtonSuccess";
import ConfirmationModal from "@/components/modals/ConfirmationModal.vue";
import Skeleton from "primevue/skeleton"
import {useI18n} from "vue-i18n";
import {useRoute, useRouter} from "vue-router";
import {required} from "@/utils/i18n-validators";
import {useVuelidate} from "@vuelidate/core";
import {useApiCall} from "@/composables/useApiCall";
import {
  modalsItemApiCall,
  createModalsItemApiCall,
  updateModalsItemApiCall,
  deleteModalsItemApiCall,
} from "@/services/settings";
import Checkbox from "primevue/checkbox";


export default defineComponent({
  layout: {name: 'AdminLayout'},
  components: {
    Checkbox,
    ButtonSuccess,
    Button,
    InputText,
    MainCard,
    Breadcrumb,
    Textarea,
    ConfirmationModal,
    Skeleton,
    Dropdown,
  },
  setup() {
    const {t} = useI18n();

    const store = useStore();
    const lang = computed(() => store.getters.getLang);
    const titleField = computed(() => !route.params.id ? 'title_ru' : `title_${lang.value}`);
    const textField = computed(() => !route.params.id ? 'content_ru' : `content_${lang.value}`);

    const router = useRouter()
    const route = useRoute()
    const errors = ref(null)
    const breadcrumbs = ref([]);
    const form = reactive({
      fields: {
        id: '',
        title_ru: '',
        title_en: '',
        content_ru: '',
        content_en: '',
        segments: []
      }
    })
    const rules = computed(() => ({
      [titleField.value]: {required},
      [textField.value]: {required},
      segments: {required},
    }));
    const v$ = useVuelidate(rules, form.fields);
    const visibleModal = ref(false);
    const isContentLoading = ref(true)
    const isUpdateLoading = computed(() =>
        isContentUpdateLoading.value ||
        isContentAddLoading.value
    )

    const {
      data: contentData,
      error: contentError,
      executeApiCall: getContentAction,
    } = useApiCall(modalsItemApiCall, true)

    const {
      isLoading: isContentUpdateLoading,
      data: contentUpdateData,
      error: contentUpdateError,
      executeApiCall: updateContentAction,
    } = useApiCall(updateModalsItemApiCall, true)

    const {
      isLoading: isContentAddLoading,
      data: contentAddData,
      error: contentAddError,
      executeApiCall: addContentAction,
    } = useApiCall(createModalsItemApiCall, true)

    const {
      isLoading: isContentDeleteLoading,
      data: contentDeleteData,
      error: contentDeleteError,
      executeApiCall: deleteContentAction,
    } = useApiCall(deleteModalsItemApiCall, true)

    const changeConfirmationStateModal = () => {
      visibleModal.value = !visibleModal.value
    };

    const toggleDestroyPartner = async () => {
      try {
        await deleteContentAction({id: route.params.id});
        if (contentDeleteData.value) {
          await router.push({name: 'settings', query: {tab: 'modals'}});
        }
      } catch (e) {
        console.error(e);
      }
    };

    const update = async () => {
      const result = await v$.value.$validate()
      errors.value = null
      if (result) {
        try {
          if (route.params.id) {
            await updateContentAction({
              fields: {
                title_ru: form.fields.title_ru,
                title_en: form.fields.title_en,
                content_ru: form.fields.content_ru,
                content_en: form.fields.content_en,
                segments: form.fields.segments,
              },
              id: route.params.id,
            })
          } else {
            await addContentAction({
              title_ru: form.fields.title_ru,
              title_en: form.fields.title_ru,
              content_ru: form.fields.content_ru,
              content_en: form.fields.content_ru,
              segments: form.fields.segments,
            })
          }
          if (contentUpdateData.value || contentAddData.value) {
            await router.push({name: 'settings', query: {tab: 'modals'}});
          }
        } catch (e) {
          console.error(e)
          if (contentUpdateError.value) {
            errors.value = contentUpdateError.value.data
          }
          if (contentAddError.value) {
            errors.value = contentAddError.value.data
          }
        }
      }
    }
    const segments = computed(() => store.getters.getListSegments || []);

    onBeforeMount(async () => {
      await store.dispatch('loadSegments');
      if (route.params.id) {
        try {
          await getContentAction({id: route.params.id})
          if (contentData.value) {
            form.fields.title_ru = contentData.value.data.title_ru
            form.fields.title_en = contentData.value.data.title_en ?? ''
            form.fields.content_ru = contentData.value.data.content_ru
            form.fields.content_en = contentData.value.data.content_en ?? ''
            form.fields.segments = contentData.value.data.segments.map(item => item.id)
          }
        } catch (e) {
          console.error(e)
        }
      }
      breadcrumbs.value = [
        {label: 'Настройки / Модальные окна', router: {name: 'settings', query: {tab: 'modals'}}},
        {label: contentData.value ? `${contentData.value.data[titleField.value]}` : 'Создание модального окна'}
      ];
      isContentLoading.value = false
    });

    return {
      t,
      form,
      breadcrumbs,
      route,
      update,
      v$,
      errors,
      visibleModal,
      isContentDeleteLoading,
      isContentLoading,
      toggleDestroyPartner,
      changeConfirmationStateModal,
      isUpdateLoading,
      titleField,
      textField,
      segments,
    };
  }
});
</script>

<template>
  <ConfirmationModal v-model:visible="visibleModal">
    <template #header>
      Удалить модальное окно
    </template>

    <template #default>
      <span class="flex mt-2">Вы уверены, что хотите удалить модальное окно?</span>
    </template>

    <template #footer>
      <div class="flex justify-content-between mt-4">
        <Button :label="t('labels.cancel')" @click="changeConfirmationStateModal"
                class="btn-primary-outlined font-light w-12"/>
        <Button :label="t('labels.destroy')" :loading="isContentDeleteLoading" @click="toggleDestroyPartner"
                class="btn-primary font-light ml-3 w-12"/>
      </div>
    </template>
  </ConfirmationModal>
  <section class="py-2 mb-3">
    <div class="flex justify-content-between align-items-center">
      <Breadcrumb :data="breadcrumbs" separator="/"/>
      <div class="flex flex-wrap gap-4">
        <button v-if="route.params.id" class="modals-item__delete-button ml-auto" @click="changeConfirmationStateModal">
          Удалить
        </button>
        <Button
            @click="update"
            :loading="!!isUpdateLoading"
            :label="route.params.id ? 'Сохранить' : 'Создать'"
            class="modals-item__save-button ml-auto"
        />
      </div>
    </div>
  </section>

  <section class="py-2 mb-3">
    <Skeleton v-if="isContentLoading" height="154px"></Skeleton>
      <MainCard v-else title="Заголовок модального окна">
            <span class="p-float-label w-full">
              <InputText
                  :class="{'p-invalid': v$[titleField].$errors?.length || errors?.[titleField] }"
                  id="name_ru"
                  class="w-full"
                  v-model="form.fields[titleField]"
              />
              <label for="name_ru">Введите заголовок <i style="color: #E26B6B">*</i></label>
            </span>
        <span v-if="v$[titleField].$errors?.length || errors?.[titleField]"
              class="color-error mt-3">{{ errors?.[titleField] ? errors[titleField][0] : t(v$[titleField].$errors?.[0].$message) }}</span>
      </MainCard>
  </section>

  <section class="py-2 mb-3">
    <Skeleton v-if="isContentLoading" height="195px"></Skeleton>
    <MainCard v-else title="Текст модального окна">
      <div class="grid">
        <div class="col-12">
          <span class="p-float-label w-full">
              <Textarea
                  :class="{'p-invalid': v$[textField].$errors?.length || errors?.[textField] }"
                  id="name_ru"
                  class="w-full"
                  rows="7"
                  v-model="form.fields[textField]"
              />
              <label for="name_ru">Введите текст <i style="color: #E26B6B">*</i></label>
            </span>
            <span v-if="v$[textField].$errors?.length || errors?.[textField]"
                class="color-error mt-3">{{ errors?.[textField] ? errors[textField][0] : t(v$[textField].$errors?.[0].$message) }}</span>
        </div>
      </div>
    </MainCard>
  </section>
  <section class="segments">
    <span class="segments__title">Отображение в сегментах</span>
    <span v-if="v$.segments.$errors?.length || errors?.segments"
          class="color-error">{{ errors?.segments ? errors.segments[0] : t(v$.segments.$errors?.[0].$message) }}</span>
    <Skeleton v-if="isContentLoading" height="195px"></Skeleton>
    <div v-else class="segments__list">
      <div v-for="item in segments" :key="item.id" class="segments__list-item">
        <Checkbox v-model="form.fields.segments" name="segment" :value="item.id" :inputId="item.title"/>
        <label :for="item.title">{{item.title}}</label>
      </div>
    </div>
  </section>
</template>

<style lang="scss" scoped>
.segments {
  display: flex;
  flex-direction: column;
  gap: 30px;
}
.segments__title {
  font-weight: 600;
  font-size: 24px;
  line-height: 32px;
  color: #333333;
}
.segments__list {
  display: grid;
  grid-template-columns: 1fr;
  background: white;
  border-radius: 10px;
  overflow: hidden;
  --gap: 1px;
  --line-offset: calc(var(--gap) / 2);
  --line-thickness: 1px;
  @media screen and (min-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media screen and (min-width: 1200px) {
    grid-template-columns: repeat(3, 1fr);
  }
}
.segments__list-item {
  position: relative;
  display: flex;
  gap: 12px;
  align-items: center;
  padding: 18px 20px;
  &:before,
  &:after {
    content: '';
    position: absolute;
    background-color: #E1E8ED;
    z-index: 1;
  }
  &:after {
    inline-size: 100vw;
    height: 1px;
    block-size: #E1E8ED;
    inset-inline-start: 0;
    inset-block-start: calc(var(--line-offset) * -2);
  }
  &:before {
    width: 1px;
    inline-size: var(--line-thickness);
    block-size: 100vh;
    inset-inline-start: calc(var(--line-offset) * -2);
  }
}
.modals-item__delete-button {
  border: 1px solid #E26B6B;
  color: #E26B6B;
  background-color: transparent;
  font-weight: 400;
  font-size: 13px;
  line-height: 22px;
  border-radius: 4px;
  padding: 12px 24px;
  cursor: pointer;
  @media screen and (min-width: 1600px) {
    padding: 12px 85px;
  }
  &:hover {
    background-color: transparent !important;
  }
}
.modals-item__save-button {
  border: 1px solid #C3987A;
  color: white;
  background-color: #C3987A;
  font-weight: 400;
  font-size: 13px;
  line-height: 22px;
  border-radius: 4px;
  padding: 12px 24px;
  cursor: pointer;
  @media screen and (min-width: 1600px) {
    padding: 12px 85px;
  }
  &:hover {
    background-color: #C3987A !important;
  }
}
.modals-item__wrapper {
  display: grid;
  gap: 30px;
  grid-template-columns: 1fr;
  @media screen and (min-width: 700px) {
    grid-template-columns: repeat(2, 1fr);
  }
}
::v-deep(.text-3xl) {
  font-weight: 600;
  font-size: 24px !important;
  line-height: 32px;
}
</style>